import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MuiAlert from '@mui/material/Alert';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  fetchCancellationReasons,
  createOrUpdateCancellationReason,
  deleteCancellationReason,
  reorderCancellationReasons,
} from '../../http/cancellationReasonAPI';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const emptyForm = { name: '', description: '', requiresComment: false };

const generateCode = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 5);

const CancellationReasons = () => {
  const [reasons, setReasons] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    load();
  }, []);

  const load = () => {
    fetchCancellationReasons().then(setReasons);
  };

  const showSnack = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFormChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;

    const code = editingId ? editingCode : generateCode();
    const payload = { ...form, code };
    if (editingId) payload.id = editingId;

    createOrUpdateCancellationReason(payload)
      .then((saved) => {
        if (editingId) {
          setReasons((prev) => prev.map((r) => (r.id === editingId ? saved : r)));
          showSnack('Причина обновлена');
        } else {
          setReasons((prev) => [...prev, saved]);
          showSnack('Причина добавлена');
        }
        setForm(emptyForm);
        setEditingId(null);
        setEditingCode(null);
      })
      .catch((err) => {
        showSnack(err?.response?.data?.message || 'Ошибка при сохранении', 'error');
      });
  };

  const handleEdit = (reason) => {
    setEditingId(reason.id);
    setEditingCode(reason.code);
    setForm({
      name: reason.name,
      description: reason.description || '',
      requiresComment: reason.requiresComment,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingCode(null);
    setForm(emptyForm);
  };

  const handleToggleActive = (reason) => {
    createOrUpdateCancellationReason({ ...reason, isActive: !reason.isActive })
      .then((saved) => {
        setReasons((prev) => prev.map((r) => (r.id === reason.id ? saved : r)));
      })
      .catch(() => showSnack('Ошибка при изменении статуса', 'error'));
  };

  const confirmDelete = () => {
    deleteCancellationReason(deleteId)
      .then(() => {
        setReasons((prev) => prev.filter((r) => r.id !== deleteId));
        setDeleteId(null);
        showSnack('Причина удалена');
      })
      .catch(() => showSnack('Ошибка при удалении', 'error'));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(reasons);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    setReasons(items);

    const reordered = items.map((r, idx) => ({ id: r.id, sortOrder: idx }));
    reorderCancellationReasons(reordered).catch(load);
  };

  return (
    <Box>
      {/* Заголовок секции */}
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Причины отмены приёма
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Управляйте списком причин отмены приёма. Пациент будет выбирать из этого списка при отмене записи в боте.
      </Typography>
      <Divider sx={{ mb: 2.5 }} />

      {/* Форма добавления/редактирования */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {editingId ? 'Редактирование причины' : 'Добавить причину'}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Название"
            value={form.name}
            onChange={handleFormChange('name')}
            size="small"
            required
            helperText="Пациент видит только его при выборе причины отмены"
          />
          <TextField
            label="Описание"
            value={form.description}
            onChange={handleFormChange('description')}
            size="small"
            helperText="Видно только в админ-панели, пациенту не показывается"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.requiresComment}
                onChange={handleFormChange('requiresComment')}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2">Требует комментарий</Typography>
                <Typography variant="caption" color="text.secondary">
                  Бот попросит пользователя оставить комментарий при выборе этой причины
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', ml: 0 }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!form.name.trim()}
            startIcon={editingId ? <EditIcon /> : <AddCircleOutlineIcon />}
            size="small"
          >
            {editingId ? 'Сохранить' : 'Добавить'}
          </Button>
          {editingId && (
            <Button variant="outlined" onClick={handleCancelEdit} size="small">
              Отмена
            </Button>
          )}
        </Box>
      </Paper>

      {/* Таблица */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="cancellation-reasons">
          {(provided) => (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell>Название</TableCell>
                    <TableCell align="center">Комментарий</TableCell>
                    <TableCell align="center">Активна</TableCell>
                    <TableCell align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                  {reasons.map((reason, index) => (
                    <Draggable key={reason.id} draggableId={String(reason.id)} index={index}>
                      {(provided, snapshot) => (
                        <TableRow
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                            opacity: reason.isActive ? 1 : 0.55,
                          }}
                        >
                          <TableCell>
                            <Tooltip title="Зажмите и перетащите для изменения порядка" arrow>
                              <Box {...provided.dragHandleProps} sx={{ cursor: 'grab', display: 'flex' }}>
                                <DragIndicatorIcon color="action" fontSize="small" />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{reason.name}</Typography>
                            {reason.description && (
                              <Typography variant="caption" color="text.secondary">
                                {reason.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {reason.requiresComment ? 'Да' : '—'}
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={reason.isActive}
                              onChange={() => handleToggleActive(reason)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleEdit(reason)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteId(reason.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Droppable>
      </DragDropContext>

      {reasons.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          Нет причин отмены. Добавьте первую.
        </Typography>
      )}

      {/* Диалог подтверждения удаления */}
      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)}>
        <DialogTitle>Удалить причину?</DialogTitle>
        <DialogContent>
          <DialogContentText>Это действие нельзя отменить.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Отмена</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CancellationReasons;
